import { Capacitor } from '@capacitor/core'
import SessionStorage from '../session-storage/SessionStorage'
import SecureSessionStorage from '../secure-session-storage/SecureSessionStorage'

export interface IAuthStorage {
  get: <T = unknown>(key: string) => Promise<T | undefined>
  set: (key: string, data: unknown, expiration?: number) => Promise<void>
  remove: (key: string) => Promise<void>
}

const webBackend: IAuthStorage = {
  get: async (key) => SessionStorage.get(key),
  set: async (key, data, expiration) => SessionStorage.set(key, data, expiration),
  remove: async (key) => SessionStorage.remove(key),
}

/**
 * Checked on every call, not cached at module load: this app ships JS over OTA
 * (see crashlyticsService.ts's canReport for the same reasoning), so a bundle
 * using this module can land on an older APK that never registered the
 * secure-storage plugin. isPluginAvailable() reflects the native shell that's
 * actually running, not just "some native platform" — falling back to
 * SessionStorage on an old APK preserves today's already-accepted behavior
 * rather than breaking auth mid-rollout.
 */
const getBackend = (): IAuthStorage =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('SecureStorage')
    ? SecureSessionStorage
    : webBackend

const AuthStorage: IAuthStorage = {
  get: (key) => getBackend().get(key),
  set: (key, data, expiration) => getBackend().set(key, data, expiration),
  remove: (key) => getBackend().remove(key),
}

export default AuthStorage
